"use client";
import VideoEmbed from "@/components/VideoEmbed";
import Image from "next/image";
import React, { useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom"; // Import the Zoom plugin

const Tabs = ({ children }) => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div className="bg-white">
      <div className="flex flex-wrap justify-center border-b border-gray-200">
        {React.Children.map(
          children,
          (child, index) =>
            React.isValidElement(child) &&
            child.props.title && (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                className={`py-2 px-2 sm:py-5 sm:px-4 focus:outline-none ${
                  activeTab === index
                    ? "border-b-2 border-blue-500 text-blue-800 font-bold text-base sm:text-lg md:text-xl"
                    : "border-b-2 border-transparent text-gray-800 hover:text-gray-700 text-base sm:text-lg md:text-xl"
                }`}
              >
                {child.props.title}
              </button>
            )
        )}
      </div>
      <div className="mt-4 p-2 sm:p-4 md:p-6">
        {React.Children.map(children, (child, index) =>
          activeTab === index ? child.props.children : null
        )}
      </div>
    </div>
  );
};

const Tab = ({ children }) => <div>{children}</div>;

export function Components({
  projectTitle,
  synopsisText,
  projectInfo,
  roles,
  images,
  informationimages,
  creditimages,
  detailedDescription,
  videoUrl = "",
  briefTitle,
  breadcrumbs,
  disclaimer,
  disclaimerTitle,
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  return (
    <>
      <style jsx global>{`
        .lightbox {
          cursor: zoom-in !important;
        }
        .lightbox.zoomed {
          cursor: zoom-out !important;
        }
      `}</style>

      <section className="bg-gray-100 py-6 sm:py-10 md:py-20 lg:py-24 overflow-hidden">
        <div className="max-w-screen-xl mx-4 sm:mx-5 lg:mx-auto">
          <div className="flex flex-col md:flex-row bg-white p-4">
            <div className="text-left md:text-left w-full md:w-1/4 pt-24">
              <h3 className="text-base lg:text-2xl">{projectTitle}</h3>
              <div className="grid grid-cols-2 space-y-2 text-sm lg:text-sm items-center justify-center">
                {projectInfo.map((info, index) => (
                  <>
                    {info.value && (
                      <>
                        <div key={index}>
                          <strong>{info.label}:</strong>
                        </div>
                        <div key={index} className="mt-0 pt-0">
                          {info.value}
                        </div>
                      </>
                    )}
                  </>
                ))}
              </div>
              <div className="mt-6 md:mt-10">
                <h1 className="text-base lg:text-base font-bold text-gray-800 mb-4 text-justify">
                  {briefTitle}
                </h1>
                <div
                  className="text-gray-700 leading-relaxed text-justify"
                  dangerouslySetInnerHTML={{ __html: synopsisText }}
                />
              </div>
            </div>
            <div className="text-left md:text-left w-full md:w-3/4">
              <Tabs>
                <Tab title="Photos">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 h-auto">
                    {images.map((image, index) => (
                      <div
                        key={index}
                        onClick={() => openLightbox(index)}
                        className="cursor-pointer group "
                      >
                        <div className="min-h-44 h-full relative ">
                          <Image
                            src={image.src}
                            fill
                            className="w-full h-auto object-cover transform transition-transform duration-300 group-hover:scale-105"
                            alt={image.alt}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 md:mt-10">
                    <h1 className="text-base lg:text-base font-bold text-gray-800 mb-4">
                      {disclaimerTitle}
                    </h1>
                    <div
                      className="text-gray-700 leading-relaxed text-[10px] text-justify"
                      dangerouslySetInnerHTML={{ __html: disclaimer }}
                    />
                  </div>
                </Tab>

                {videoUrl && (
                  <Tab title="Video">
                    <div className="text-black flex flex-col gap-4 sm:gap-6 md:flex-row md:gap-8 p-4">
                      {videoUrl && <VideoEmbed videoUrl={videoUrl} />}
                    </div>
                  </Tab>
                )}

                <Tab title="Description">
                  <div className="text-black">
                    <div className="mt-6 md:mt-10">
                      <div
                        className="text-gray-700 leading-relaxed text-justify"
                        dangerouslySetInnerHTML={{
                          __html: detailedDescription,
                        }}
                      />
                    </div>
                  </div>
                </Tab>
              </Tabs>
            </div>
          </div>
        </div>
      </section>

      {lightboxOpen && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          slides={images.map((image) => ({ src: image.src }))}
          index={currentImageIndex}
          onIndexChange={setCurrentImageIndex}
          plugins={[Zoom]} // Add the Zoom plugin
          className="lightbox"
          zoom={{
            maxZoomPixelRatio: 5,
            onZoomChange: (isZoomed) => {
              const lightbox = document.querySelector(
                ".lightbox lightbox.zoomed"
              );
              if (lightbox) {
                if (isZoomed) {
                  lightbox.classList.add("zoomed");
                } else {
                  lightbox.classList.remove("zoomed");
                }
              }
            },
          }}
        />
      )}
    </>
  );
}

export default Components;
